/**
 * Platform Settings API — GET and PUT /v3/platform-settings
 * Single MongoDB document (id="main") storing all platform-wide config.
 */
import apiClient from './client';

export interface FileAnswerSettings {
  allowedTypes: string;   // comma-separated extensions, '' = any
  maxSizeMb: number;      // 1–500
  retentionDays: number;  // days to keep student file answers
}

export interface RecordingSettings {
  maxAudioDurationSec: number;  // 30–600
  maxVideoDurationSec: number;  // 30–900
  autoStopEnabled: boolean;     // auto-stop when limit reached
}

export interface DataRetentionSettings {
  studentAnswerRetentionDays: number;  // 1–365, 0 = keep forever
  autoDelete: boolean;                 // actually delete vs just warn
}

export interface RegistrationSettings {
  allowTeacherSelfRegistration: boolean;
  requireEmailVerification: boolean;
}

export interface VocabularySettings {
  enabled: boolean;  // show «Мой словарь» in nav for students AND teachers
}

export interface CertificateSettings {
  templateStyle: 'gold' | 'blue' | 'classic' | 'minimal';
  titleRu: string;
  titleEn: string;
  subtitleText: string;
  bodyText: string;
  signatoryName: string;
  signatoryRole: string;
  accentColor: string;
  showQrCode: boolean;
  showStamp: boolean;
  showAuthorName: boolean;
  logoUrl?: string;
}

export interface PlatformSettings {
  fileAnswer: FileAnswerSettings;
  recording: RecordingSettings;
  dataRetention: DataRetentionSettings;
  registration: RegistrationSettings;
  vocabulary: VocabularySettings;
  certificate: CertificateSettings;
  /** Разделы админки доступные для роли moderator */
  moderatorSections: string[];
  _updated_at?: string;
  _updated_by?: string;
}

export const DEFAULT_PLATFORM_SETTINGS: PlatformSettings = {
  fileAnswer: {
    allowedTypes: 'pdf, doc, docx, jpg, jpeg, png, mp3, mp4',
    maxSizeMb: 50,
    retentionDays: 7,
  },
  recording: {
    maxAudioDurationSec: 180,
    maxVideoDurationSec: 300,
    autoStopEnabled: true,
  },
  dataRetention: {
    studentAnswerRetentionDays: 30,
    autoDelete: false,
  },
  registration: {
    allowTeacherSelfRegistration: true,
    requireEmailVerification: false,
  },
  vocabulary: {
    enabled: true,
  },
  certificate: {
    templateStyle: 'gold',
    titleRu: 'СЕРТИФИКАТ ОБ ОКОНЧАНИИ',
    titleEn: 'CERTIFICATE OF COMPLETION',
    subtitleText: 'Настоящим подтверждается, что',
    bodyText: 'успешно завершил(а) полный курс обучения по программе',
    signatoryName: 'Академия English Patient',
    signatoryRole: 'Руководитель учебной программы',
    accentColor: '#2d5be3',
    showQrCode: true,
    showStamp: true,
    showAuthorName: true,
    logoUrl: '',
  },
  moderatorSections: ['market', 'tags', 'songs', 'trainers', 'trainers-moderation', 'materials', 'certificates'],
};

const PlatformSettingsAPI = {
  async get(): Promise<PlatformSettings> {
    const { data } = await apiClient.get('/v3/platform-settings');
    const raw = (data as any)?.result ?? data ?? {};
    // Deep-merge with defaults so missing keys don't break the UI
    return {
      fileAnswer:    { ...DEFAULT_PLATFORM_SETTINGS.fileAnswer,    ...(raw.fileAnswer    ?? {}) },
      recording:     { ...DEFAULT_PLATFORM_SETTINGS.recording,     ...(raw.recording     ?? {}) },
      dataRetention: { ...DEFAULT_PLATFORM_SETTINGS.dataRetention, ...(raw.dataRetention ?? {}) },
      registration:  { ...DEFAULT_PLATFORM_SETTINGS.registration,  ...(raw.registration  ?? {}) },
      vocabulary:    { ...DEFAULT_PLATFORM_SETTINGS.vocabulary,    ...(raw.vocabulary    ?? {}) },
      certificate:   { ...DEFAULT_PLATFORM_SETTINGS.certificate,   ...(raw.certificate   ?? {}) },
      moderatorSections: raw.moderatorSections ?? DEFAULT_PLATFORM_SETTINGS.moderatorSections,
      _updated_at:   raw._updated_at,
      _updated_by:   raw._updated_by,
    };
  },

  async save(settings: PlatformSettings): Promise<PlatformSettings> {
    const { data } = await apiClient.put('/v3/platform-settings', settings);
    const raw = (data as any)?.result ?? data ?? {};
    return {
      fileAnswer:    { ...DEFAULT_PLATFORM_SETTINGS.fileAnswer,    ...(raw.fileAnswer    ?? {}) },
      recording:     { ...DEFAULT_PLATFORM_SETTINGS.recording,     ...(raw.recording     ?? {}) },
      dataRetention: { ...DEFAULT_PLATFORM_SETTINGS.dataRetention, ...(raw.dataRetention ?? {}) },
      registration:  { ...DEFAULT_PLATFORM_SETTINGS.registration,  ...(raw.registration  ?? {}) },
      vocabulary:    { ...DEFAULT_PLATFORM_SETTINGS.vocabulary,    ...(raw.vocabulary    ?? {}) },
      certificate:   { ...DEFAULT_PLATFORM_SETTINGS.certificate,   ...(raw.certificate   ?? {}) },
      moderatorSections: raw.moderatorSections ?? DEFAULT_PLATFORM_SETTINGS.moderatorSections,
      _updated_at:   raw._updated_at,
      _updated_by:   raw._updated_by,
    };
  },
};

export default PlatformSettingsAPI;
